import { ICompatibilityResult, ILkdpiInfo, IPotentialDonor, IRecipient, ISingleEpletMmRisk } from '@nkr/models/index';
import { DonorType } from '@nkr/enums/donor-type';
import { Button, Glyphicon, OverlayTrigger } from 'react-bootstrap';
import { TableWrapper } from '../../center-admin/billing/styles';
import HlaMatches from '@nkr/views/match/hla-matches';
import LkdpiInfo from '@nkr/components/lkdpi-info';
import PopoverTemplate from '@nkr/components/popover-template';
import SingleEpletMmRisk from '../../swap-management/single-eplet-mm-risk';

interface IProps {
    centerId: number,
    recipientId: number
    recipient: IRecipient,
    donor: IPotentialDonor,
    donorCompatibilityResult: ICompatibilityResult,
    singleEpletMmRisk: ISingleEpletMmRisk,
    lkdpiInfo: ILkdpiInfo,
    avoids: any[],
}

const WaitlistReplaceCurrentDonor = (props: IProps) => {
    const getDonorKidneyType = () => {
        if (props.donor != null) {
            if (props.donor.whichKidney == 'L-opt') {
                return '(R)';
            } else {
                if (props.donor.whichKidney == 'R-opt') {
                    return '(L)';
                }
            }
        }

        return '';
    };

    const getWhichKidney = () => {
        let arteryCount = props.donor.numberOfArteries;
        let arteryLabel = arteryCount > 1 ? 'arteries' : 'artery';

        switch (props.donor.whichKidney) {
            case 'Left': return <p>Left kidney with ({arteryCount}) {arteryLabel}</p>;
            case 'Right': return <p>Right kidney with ({arteryCount}) {arteryLabel}</p>;
            case 'L-opt': return <p>Left kidney with ({arteryCount}) {arteryLabel} but right kidney is available</p>;
            case 'R-opt': return <p>Right kidney with ({arteryCount}) {arteryLabel} but left kidney is available</p>;
        }
    };

    const latOverlay = (
        <PopoverTemplate width='350px'>Which kidney is available (L/R), plus the number of arteries, and if possible if the other kidney is also available</PopoverTemplate>
    );

    const lkdpiOverlay = (
        <PopoverTemplate width='350px'>
            <p className='text-center'>
                <b>Donor-Recipient matches with a lower LKDPI are associated with a lower risk of graft loss</b>
            </p>
            <p className='text-center'>Research can be found at:</p>
            <p className='text-center'><a href='https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6114098/' target='_blank'>A Risk Index for Living Donor Kidney Transplantation</a></p>
        </PopoverTemplate>
    );

    const popOverFocus = (
        <HlaMatches
            donor={props.donor}
            recipient={props.recipient}
            compatibilityResult={props.donorCompatibilityResult}
            showAvoidsList={true}
            showSmaller={true}
        />
    );

    const donorKidneyType = getDonorKidneyType();

    return (
        <TableWrapper bordered condensed striped>
            <thead>
                <tr>
                    <th colSpan={14}>Current Donor</th>
                </tr>
                <tr>
                    <th style={{ textAlign: 'center' }}>Alias</th>
                    <th style={{ textAlign: 'center' }}>Center</th>
                    <th style={{ textAlign: 'center' }}>PMPc</th>
                    <th style={{ textAlign: 'center' }}>State</th>
                    <th style={{ textAlign: 'center' }}>ABO</th>
                    <th style={{ textAlign: 'center' }}>Age</th>
                    <th style={{ textAlign: 'center' }}>Wt</th>
                    <th style={{ textAlign: 'center' }}>
                        Lat
                        <OverlayTrigger rootClose placement='bottom' trigger='click' overlay={latOverlay}>
                            <Glyphicon glyph='info-sign' />
                        </OverlayTrigger>
                    </th>
                    <th style={{ textAlign: 'center' }}>CC</th>
                    <th style={{ textAlign: 'center' }}>Hla Pts</th>
                    <th style={{ textAlign: 'center' }}>
                        LKDPI
                        <OverlayTrigger rootClose placement='bottom' trigger='click' overlay={lkdpiOverlay}>
                            <Glyphicon glyph='info-sign' />
                        </OverlayTrigger>
                    </th>
                    <th style={{ textAlign: 'center' }}>Eplet MM</th>
                    <th style={{ textAlign: 'center' }}>Avoids</th>
                    <th style={{ textAlign: 'center' }}>Evaluation</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{props.donor.alias}</td>
                    <td>{props.donor.center.alias} {props.donor.rdnCenter != null ? <>(<span style={{ color: 'red' }}>{props.donor.rdnCenter.alias}</span>)</> : null}</td>

                    {props.donor.donorTypeId == DonorType.DirectedDonor && props.donor.pairMatchPower != null &&
                        <td>{props.donor.pairMatchPower.toFixed(0)}</td>
                    }

                    {(props.donor.donorTypeId != DonorType.DirectedDonor || props.donor.pairMatchPower == null) &&
                        <td>N/A</td>
                    }

                    <td>{props.donor.state}</td>
                    <td>{props.donor.fullBloodType}</td>
                    <td>{props.donor.age}</td>
                    <td>{props.donor.weight}</td>
                    <td>
                        <OverlayTrigger rootClose placement='bottom' trigger='click' overlay={<PopoverTemplate>{getWhichKidney()}</PopoverTemplate>}>
                            <a style={{ color: '#337ab7', cursor: 'default' }}>
                                {props.donor.whichKidneyAbbreviated}{props.donor.numberOfArteries + donorKidneyType}
                            </a>
                        </OverlayTrigger>
                    </td>
                    <td>{props.donor.creatinineClearance}</td>
                    <td>
                        <OverlayTrigger rootClose placement='bottom' trigger='click'
                            overlay={<PopoverTemplate>{popOverFocus}</PopoverTemplate>}>
                            <a style={{ color: '#337ab7', cursor: 'default' }}>{props.donorCompatibilityResult.totalHlaPoints}</a>
                        </OverlayTrigger>
                    </td>
                    <td>
                        <LkdpiInfo donor={props.donor}
                            recipient={props.donorCompatibilityResult.recipient}
                            info={props.lkdpiInfo}
                            placement='bottom'
                        />
                    </td>

                    <td><SingleEpletMmRisk data={props.singleEpletMmRisk} isAdmin={false} centerId={props.centerId} placement='bottom' /></td>
                    <td>{props.avoids.length}</td>
                    <td>
                        <a href={'/center/' + props.centerId + '/recipient/' + props.recipientId + '/evaluate-donor/' + props.donor.id} target='_blank'>
                            <Button bsStyle="primary btn-xs">View</Button>
                        </a>
                    </td>
                </tr>
            </tbody>
        </TableWrapper>
    );
}

export default WaitlistReplaceCurrentDonor;